import { useStyles, Button } from '@archipro-design/aria';
import { EyeLight, EyeSlashLight } from '@archipro-design/icons';
import * as S from './DetailPageButtons.style';
import { IslandTooltip } from './IslandTooltip';
import * as React from 'react';
import { createPortal } from 'react-dom';
import DeleteItemConfirmModal from '~/modules/directory/component/DeleteItemConfirmModal';

export type VisibilityItemType = 'project' | 'product';

interface VisibilityToggleButtonProps {
    isInDirectory: boolean;
    itemType: VisibilityItemType;
    onToggle: (inLibrary: boolean) => void;
    /**
     * When true the button is read-only: clicks don't open the confirm
     * modal, the icon is forced to "Profile Only", and the tooltip
     * explains why. Use when the owning professional isn't in the
     * directory so the item's effective visibility is locked to profile.
     */
    disabled?: boolean;
    /** Extra line appended to the remove-from-directory confirm copy. */
    removalNote?: string;
}

const DISABLED_TOOLTIP = {
    project:
        'This project is profile only because its professional is not enabled or published. Enable the professional to make the project visible in the directory.',
    product:
        'This product is profile only because its professional is not enabled or published. Enable the professional to make the product visible in the directory.',
} as const;

const CONFIRM_COPY = {
    addToDirectory: {
        project:
            'Are you sure you want to add this project to the directory page? This project will also be visible in search results.',
        product:
            'Are you sure you want to add this product to the directory page? This product will also be visible in search results.',
    },
    removeFromDirectory: {
        project:
            'Removing this project from the directory will also hide it from search results. It will still be visible on profile.',
        product:
            'Removing this product from the directory will also hide it from search results. It will still be visible on profile.',
    },
} as const;

export const VisibilityToggleButton = ({
    isInDirectory,
    itemType,
    onToggle,
    disabled = false,
    removalNote,
}: VisibilityToggleButtonProps) => {
    const { css } = useStyles();
    const [confirmOpen, setConfirmOpen] = React.useState(false);

    React.useEffect(() => {
        if (confirmOpen) {
            document.documentElement.style.scrollbarGutter = 'stable';
        } else {
            document.documentElement.style.scrollbarGutter = '';
        }
        return () => {
            document.documentElement.style.scrollbarGutter = '';
        };
    }, [confirmOpen]);

    const effectiveInDirectory = isInDirectory && !disabled;
    // Reads as a visibility switch: an open eye when the item is in the
    // directory, a struck-through one when it is profile only.
    const icon = effectiveInDirectory ? <EyeLight /> : <EyeSlashLight />;
    const tooltipLabel = disabled
        ? DISABLED_TOOLTIP[itemType]
        : isInDirectory
          ? 'In Directory'
          : 'Profile Only';

    const confirmContent = isInDirectory
        ? [CONFIRM_COPY.removeFromDirectory[itemType], removalNote]
              .filter(Boolean)
              .join(' ')
        : CONFIRM_COPY.addToDirectory[itemType];

    const handleConfirm = () => {
        setConfirmOpen(false);
        onToggle(!isInDirectory);
    };

    const handleClick = () => {
        if (disabled) return;
        setConfirmOpen(true);
    };

    return (
        <>
            <IslandTooltip label={tooltipLabel}>
                <Button
                    aria-label={tooltipLabel}
                    className={css(S.ActionButton)}
                    onClick={handleClick}
                    icon={icon}
                    size={20}
                    color="white"
                    variables={{
                        iconPadding: 0,
                        // 13px at the 1920 design frame. In rem, not a raw px string,
                        // so it scales with the vw-anchored root like the numeric
                        // tokens do; it holds the circle at its original size now
                        // that the icon inside it is smaller.
                        buttonPadding: '0.8125rem',
                        buttonRadius: `50%`,
                    }}
                />
            </IslandTooltip>
            {confirmOpen &&
                typeof document !== 'undefined' &&
                createPortal(
                    <DeleteItemConfirmModal
                        open={confirmOpen}
                        title={
                            isInDirectory
                                ? 'Remove from directory?'
                                : 'Add to directory?'
                        }
                        content={confirmContent}
                        onCancel={() => setConfirmOpen(false)}
                        onConfirm={handleConfirm}
                        confirmLabel="Continue"
                        cancelLabel="Cancel"
                    />,
                    document.body,
                )}
        </>
    );
};
